import { Head } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Mail, Smartphone, Shield, ArrowLeft } from 'lucide-react'
import AuthLayout from '@/components/AuthLayout'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface TwoFactorSetupProps {
  enableEmailUrl: string
  setupAppUrl: string
  backUrl: string
  locale: string
  logoUrl: string
  translations: {
    setupTitle: string
    chooseMethod: string
    methodEmail: string
    methodEmailDesc: string
    methodApp: string
    methodAppDesc: string
    chooseEmail: string
    chooseApp: string
    back: string
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function TwoFactorSetup({
  enableEmailUrl,
  setupAppUrl,
  backUrl,
  locale,
  logoUrl,
  translations: t,
}: TwoFactorSetupProps) {
  return (
    <>
      <Head title={t.setupTitle} />

      <AuthLayout locale={locale} logoUrl={logoUrl}>
        <div className="rounded-2xl bg-white shadow-xl shadow-slate-200/60 border border-slate-100 p-8 sm:p-10">
          <div className="text-center mb-8">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#2DD4BF]/10 to-[#8B5CF6]/10">
              <Shield className="h-7 w-7 text-[#252854]" />
            </div>
            <h1 className="text-2xl font-semibold text-[#252854] mb-1">{t.setupTitle}</h1>
            <p className="text-sm text-slate-400">{t.chooseMethod}</p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {/* Email method */}
            <div className="group relative rounded-xl border-2 border-slate-100 hover:border-[#2DD4BF]/40 bg-slate-50/30 hover:bg-[#2DD4BF]/[0.03] p-6 text-center transition-all duration-200 cursor-pointer">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-[#2DD4BF]/10">
                <Mail className="h-6 w-6 text-[#2DD4BF]" />
              </div>
              <h4 className="font-semibold text-[#252854] mb-1">{t.methodEmail}</h4>
              <p className="text-xs text-slate-400 mb-4 leading-relaxed">{t.methodEmailDesc}</p>
              <form method="post" action={enableEmailUrl}>
                <Button
                  type="submit"
                  variant="outline"
                  className="w-full rounded-lg border-[#2DD4BF]/30 text-[#2DD4BF] hover:bg-[#2DD4BF]/10 hover:text-[#14B8A6] hover:border-[#2DD4BF]/50 transition-all"
                >
                  {t.chooseEmail}
                </Button>
              </form>
            </div>

            {/* App method */}
            <div className="group relative rounded-xl border-2 border-slate-100 hover:border-[#8B5CF6]/40 bg-slate-50/30 hover:bg-[#8B5CF6]/[0.03] p-6 text-center transition-all duration-200 cursor-pointer">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-[#8B5CF6]/10">
                <Smartphone className="h-6 w-6 text-[#8B5CF6]" />
              </div>
              <h4 className="font-semibold text-[#252854] mb-1">{t.methodApp}</h4>
              <p className="text-xs text-slate-400 mb-4 leading-relaxed">{t.methodAppDesc}</p>
              <a href={setupAppUrl}>
                <Button
                  variant="outline"
                  className="w-full rounded-lg border-[#8B5CF6]/30 text-[#8B5CF6] hover:bg-[#8B5CF6]/10 hover:text-[#7C3AED] hover:border-[#8B5CF6]/50 transition-all"
                >
                  {t.chooseApp}
                </Button>
              </a>
            </div>
          </div>

          <div className="mt-6 text-center">
            <a
              href={backUrl}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#8B5CF6] hover:text-[#7C3AED] transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {t.back}
            </a>
          </div>
        </div>
      </AuthLayout>
    </>
  )
}
